import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import {
  Search,
  HelpCircle,
  FileText,
  MessageSquare,
  Mail,
  Phone,
  ArrowLeft,
  Briefcase,
  Users,
  CreditCard,
  Shield,
  Book
} from 'lucide-react';

export function HelpCenter() {
  const categories = [
    { icon: Users, title: 'Getting Started', description: 'Basic guides for new users', count: 12 },
    { icon: Briefcase, title: 'For Employers', description: 'Hiring and job posting help', count: 18 },
    { icon: Users, title: 'For Candidates', description: 'Job search and applications', count: 15 },
    { icon: CreditCard, title: 'Billing & Plans', description: 'Subscription and payment info', count: 8 },
    { icon: Shield, title: 'Privacy & Security', description: 'Data protection and safety', count: 10 },
    { icon: Book, title: 'Features Guide', description: 'Platform features explained', count: 20 },
  ];

  const faqs = {
    employers: [
      {
        question: 'How do I post a job?',
        answer: 'To post a job, log in to your employer dashboard, click "Post New Job", and fill in the job details. You can use our AI assistant to generate a professional job description based on your requirements. Free accounts get 1 job posting per month, while paid plans offer unlimited postings.'
      },
      {
        question: 'How does AI candidate matching work?',
        answer: 'Our AI analyzes candidate profiles, experience, skills, and qualifications to provide a match score for each applicant. The system considers location, salary expectations, availability, and specific job requirements to find the best candidates for your role.'
      },
      {
        question: 'Can I message candidates directly?',
        answer: 'Yes! Once a candidate applies or you shortlist them, you can send messages through our secure, end-to-end encrypted messaging system. This feature is available on Professional and Enterprise plans.'
      },
      {
        question: 'What is the difference between free and paid job posts?',
        answer: 'Free posts are visible for 30 days with standard visibility. Paid boost posts appear at the top of search results, get highlighted with a special badge, and receive priority in candidate recommendations.'
      },
    ],
    candidates: [
      {
        question: 'How do I create a CV using AI?',
        answer: 'Navigate to the Career Path section in your dashboard and click "Generate CV with AI". Our system will create a professional CV based on your profile, work experience, skills, and qualifications. The generated CV includes the Lighthouse Jobs branding.'
      },
      {
        question: 'What is the AI job matching score?',
        answer: 'The match score shows how well your profile aligns with a job posting. It considers your experience, qualifications, skills, location, and salary expectations. Higher scores (90%+) indicate excellent matches that you should prioritize.'
      },
      {
        question: 'How does the Interview Assistant work?',
        answer: 'The Interview Assistant is an AI chatbot that helps you prepare for interviews. Ask it questions about common interview topics, practice answers, or get advice on specific interview situations. It\'s available 24/7 in your dashboard.'
      },
      {
        question: 'Can I apply as a team?',
        answer: 'Yes! When registering, check the "Register as a team" option. This allows multiple people to share one account and apply for jobs together, which is perfect for care teams or group employment.'
      },
    ],
    general: [
      {
        question: 'Is my data secure?',
        answer: 'Absolutely. We use industry-standard encryption for all data transmission and storage. Messages between employers and candidates are end-to-end encrypted. We never share your personal information with third parties without your explicit consent.'
      },
      {
        question: 'How do I upgrade my plan?',
        answer: 'Go to the Subscription Portal from your dashboard. Compare the available plans and click "Upgrade" on your preferred option. You can change or cancel your subscription at any time.'
      },
      {
        question: 'What payment methods do you accept?',
        answer: 'We accept all major credit and debit cards (Visa, Mastercard, American Express), PayPal, and bank transfers for Enterprise plans. All payments are processed securely through our payment partners.'
      },
      {
        question: 'How does the Returner Hub work?',
        answer: 'The Returner Hub is designed for parents, carers, and long-term unemployed individuals returning to work. It provides specialized job recommendations, flexible working options, and connections to support services for childcare, wellbeing, and financial advice.'
      },
    ]
  };

  return (
    <div className="relative min-h-screen pt-16 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]" />
      {/* Header */}
      <header className="border-b border-white/10 bg-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="mb-4 text-gold-gradient">
              Help Center
            </h1>
            <p className="text-gray-400 mb-8">
              Find answers to your questions and get the most out of Lighthouse Jobs
            </p>
            
            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search for help articles..."
                className="pl-12 h-14 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Categories */}
        <section className="mb-16">
          <h2 className="mb-8 text-center text-gold-gradient">
            Browse by Category
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <Card
                  key={index}
                  className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300 cursor-pointer group"
                >
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-gold-gradient flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="w-6 h-6 text-black" />
                    </div>
                    <h3 className="text-white mb-2">{category.title}</h3>
                    <p className="text-sm text-gray-400 mb-3">{category.description}</p>
                    <span className="text-xs text-[#ba9036]">{category.count} articles</span>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* FAQs */}
        <section className="mb-16">
          <h2 className="mb-8 text-center text-gold-gradient">
            Frequently Asked Questions
          </h2>
          
          <Tabs defaultValue="general" className="max-w-4xl mx-auto">
            <TabsList className="grid grid-cols-3 bg-white/5 border border-white/10 mb-8">
              <TabsTrigger value="general" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
                General
              </TabsTrigger>
              <TabsTrigger value="employers" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
                For Employers
              </TabsTrigger>
              <TabsTrigger value="candidates" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
                For Candidates
              </TabsTrigger>
            </TabsList>

            {Object.entries(faqs).map(([key, questions]) => (
              <TabsContent key={key} value={key}>
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-6">
                    <Accordion type="single" collapsible className="space-y-2">
                      {questions.map((faq, index) => (
                        <AccordionItem
                          key={index}
                          value={`item-${index}`}
                          className="border-white/10"
                        >
                          <AccordionTrigger className="text-white hover:text-[#ba9036]">
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-300">
                            {faq.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </section>

        {/* Contact Support */}
        <section>
          <h2 className="mb-8 text-center text-gold-gradient">
            Still Need Help?
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg bg-gold-gradient flex items-center justify-center mb-4">
                  <MessageSquare className="w-6 h-6 text-black" />
                </div>
                <CardTitle className="text-white">Live Chat</CardTitle>
                <CardDescription className="text-gray-400">
                  Chat with our support team
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="w-full bg-gold-gradient hover:brightness-110 text-black">
                  Start Chat
                </Button>
                <p className="text-xs text-gray-500 text-center mt-3">
                  Available Mon-Fri, 9am-6pm
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg bg-gold-gradient flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-black" />
                </div>
                <CardTitle className="text-white">Email Support</CardTitle>
                <CardDescription className="text-gray-400">
                  Send us a detailed message
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/5"
                >
                  Send Email
                </Button>
                <p className="text-xs text-gray-500 text-center mt-3">
                  Response within 24 hours
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg bg-gold-gradient flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6 text-black" />
                </div>
                <CardTitle className="text-white">Phone Support</CardTitle>
                <CardDescription className="text-gray-400">
                  Call us for urgent matters
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/5"
                >
                  Call Now
                </Button>
                <p className="text-xs text-gray-500 text-center mt-3">
                  Enterprise customers only
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </div>
  );
}
